import cn from 'classnames';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import SimpleHeadline from 'components/simple-headline';
import { SegmentationIds } from 'constants/segmentation';
import { PetContextValues } from 'contexts/pet';
import { useSegmentationContext } from 'contexts/segmentation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { gtm, selectElement } from 'tracking';
import useScrollTracking from 'tracking/use-scroll-tracking';
import CollectionSectionBestseller from '../collection-section-bestseller';
import SliderButtons from '../slider-buttons';
import styles from './landing-page-collection-slider.module.css';

type CollectionMap = {
  dogs: Array<CollectionInfo>;
  cats: Array<CollectionInfo>;
};
interface CollectionInfo {
  collectionHandle: string;
  ctaText?: string;
  redirectLink?: string;
  slideTitle?: string;
  title: string;
}

const PRODUCTION_DOG_COLLECTIONS = [
  {
    title: 'landingpage:slider-selection-pickyeater',
    collectionHandle: 'fur-hunde-wahlerisch',
    redirectLink: '/collections/fur-hunde-sehr-waehlerisch',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: 'landingpage:slider-selection-digestion',
    collectionHandle: 'fur-hunde-verdauungsprobleme',
    redirectLink: '/collections/fur-hunde-verdauungsprobleme',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: 'landingpage:slider-selection-intolerance',
    collectionHandle: 'fur-hunde-unvertraeglichkeiten-allergien',
    redirectLink: '/collections/fur-hunde-unvertraeglichkeiten-allergien',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: 'landingpage:slider-selection-skin-fur',
    collectionHandle: 'fur-hunde-haut-fellprobleme',
    redirectLink: '/collections/fur-hunde-haut-fellprobleme',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: 'landingpage:slider-selection-overweight',
    collectionHandle: 'fur-hunde-uebergewicht',
    redirectLink: '/collections/fur-hunde-uebergewicht',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: 'landingpage:slider-selection-junior',
    collectionHandle: 'fur-hunde-welpen-und-junghunde',
    redirectLink: '/collections/fur-hunde-welpen-und-junghunde',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: 'landingpage:slider-selection-senior',
    collectionHandle: 'fur-hunde-senior',
    redirectLink: '/collections/fur-hunde-senior',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
];

const PRODUCTION_CAT_COLLECTIONS = [
  {
    title: `landingpage:slider-selection:${SegmentationIds.CatPickyEater}`,
    collectionHandle: 'fur-katzen-mehr-akzeptanz',
    redirectLink: '/collections/fur-katzen-wahlerisch',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: `landingpage:slider-selection:${SegmentationIds.CatDigestion}`,
    collectionHandle: 'fur-katzen-verdauungsprobleme',
    redirectLink: '/collections/fur-katzen-verdauungsprobleme',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: `landingpage:slider-selection:${SegmentationIds.IntoleranceCat}`,
    collectionHandle: 'fur-katzen-unvertraeglichkeiten',
    redirectLink: '/collections/fur-katzen-unvertraeglichkeiten',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: `landingpage:slider-selection:${SegmentationIds.CatOverweight}`,
    collectionHandle: 'fur-katzen-ubergewicht',
    redirectLink: '/collections/fur-katzen-ubergewicht',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: `landingpage:slider-selection:${SegmentationIds.SkinFurCat}`,
    collectionHandle: 'fur-katzen-haut-fellprobleme',
    redirectLink: '/collections/fur-katzen-haut-fellprobleme',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: `landingpage:slider-selection:${SegmentationIds.Kitten}`,
    collectionHandle: 'fur-katzen-kitten-und-junge-katze',
    redirectLink: '/collections/fur-katzen-kitten-und-junge-katze',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
  {
    title: `landingpage:slider-selection:${SegmentationIds.CatSenior}`,
    collectionHandle: 'fur-katzen-senior',
    redirectLink: '/collections/fur-katzen-senior',
    ctaText: 'landingpage:nutrition-needs-guidance:last-slide-redirect',
  },
];

const PRODUCTION_COLLECTION_MAP: CollectionMap = {
  dogs: PRODUCTION_DOG_COLLECTIONS,
  cats: PRODUCTION_CAT_COLLECTIONS,
};

const COLLECTIONS_MAP: CollectionMap = PRODUCTION_COLLECTION_MAP;

interface LandingPageCollectionSliderProps {
  descriptionText?: React.ReactNode;
  elementNameTracking?: string[];
  petType: PetContextValues;
  title: string;
}

/**
 * Production slider with pets need filter button
 * parent html tag should have overflow-hidden class for slider implementation
 */
const LandingPageCollectionSlider: React.FC<
  LandingPageCollectionSliderProps
> = ({ descriptionText, elementNameTracking, petType, title }) => {
  const { push } = useRouter();

  const collections = useMemo(() => {
    if (petType === 'dogs') {
      return COLLECTIONS_MAP['dogs'];
    } else {
      return COLLECTIONS_MAP['cats'];
    }
  }, [petType]);
  const [selectedCollection, setSelectedCollection] = useState<CollectionInfo>(
    collections[0]
  );

  const { pushSegmentation, getSegmentationFromUrl } = useSegmentationContext();

  const updateSegmentation = useCallback(
    (redirectLink: CollectionInfo['redirectLink']) => {
      if (redirectLink) {
        const segmentation = getSegmentationFromUrl({
          url: redirectLink,
        });
        if (segmentation) {
          pushSegmentation({
            segmentation: segmentation.id,
          });
        }
      }
    },
    [getSegmentationFromUrl, pushSegmentation]
  );

  const ref = useRef<HTMLDivElement>(null);
  useScrollTracking(
    ref,
    elementNameTracking ?? [
      'Selectable List',
      selectedCollection.collectionHandle,
    ]
  );

  const lastSlide = selectedCollection.redirectLink && (
    <Link
      href={selectedCollection.redirectLink}
      onClick={() => updateSegmentation(selectedCollection.redirectLink)}
      className="focus-default border-grey-100 flex h-full w-full cursor-pointer rounded-lg border decoration-transparent lg:hover:shadow-sm"
    >
      <div className="relative flex grow flex-col items-center justify-evenly">
        {selectedCollection.slideTitle && (
          <div className="text-secondary-700 px-2 text-xl font-medium">
            <FormattedMessage id={selectedCollection.slideTitle} />
          </div>
        )}
        <div className="text-secondary-700 bg-other-white flex h-18 items-center rounded-lg px-3 text-center text-base leading-tight font-bold">
          <FormattedMessage id={selectedCollection.ctaText} />
        </div>
      </div>
    </Link>
  );

  return (
    <ImpressionTrackerNew
      element={
        elementNameTracking ?? [
          'Selectable List',
          selectedCollection.collectionHandle,
        ]
      }
    >
      <>
        <SimpleHeadline title={title} />

        {/* add negative margin so that two texts are closer without changing line height */}
        {descriptionText && (
          <p className="text-primary-main mt-[-0.5rem] mb-10 text-base">
            {descriptionText}
          </p>
        )}

        <div
          className={cn(
            'mx-[-1.5rem] mb-3 flex snap-x overflow-scroll',
            styles.scrollbarHide
          )}
          ref={ref}
        >
          <SliderButtons
            className="mt-2 px-6"
            collections={collections}
            currentCollection={selectedCollection}
            trackingName={elementNameTracking}
            handleClicked={(collection) => {
              if (
                selectedCollection &&
                collection.collectionHandle !==
                  selectedCollection.collectionHandle
              ) {
                setSelectedCollection(collection);
                updateSegmentation(collection.redirectLink);
                gtm(
                  selectElement({
                    element: [
                      ...(elementNameTracking ?? ['Selectable List']),
                      collection.collectionHandle,
                    ],
                  })
                );
              }
            }}
          />
        </div>

        {/* @TODO : current Implementation triggers SWR validation on every click */}
        <CollectionSectionBestseller
          collection={selectedCollection.collectionHandle}
          elementNameTracking={elementNameTracking}
          shouldShowLoading={true}
          // we need to pass down this option so that first render can provide collection
          swrOptions={{ revalidateOnMount: true }}
          lastSlide={lastSlide}
          isHomePage
          onTileClick={(product) => {
            // update segmentation
            updateSegmentation(selectedCollection.redirectLink);
            if (!product) {
              throw new Error('product should be defined');
            }
            push(`/products/${product.handle}`);
          }}
        />
      </>
    </ImpressionTrackerNew>
  );
};

export default LandingPageCollectionSlider;
